import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import { People as PeopleIcon, Add as AddIcon } from '@mui/icons-material';
import { useCanAccessAdmin, useIsAdmin, useHasRole } from '../../../contexts/PermissionsContext';
import AdminLayout from '../../../layouts/AdminLayout';
import OrganizationMembersTable from '../../../components/admin/OrganizationMembersTable';
import AddMemberDialog from '../../../components/admin/AddMemberDialog';
import unifiedApi from '../../../utils/unifiedApi';

const OrganizationMembersPage = () => {
  const canAccessAdmin = useCanAccessAdmin();
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useHasRole('super-admin');
  
  const [viewFilter, setViewFilter] = useState('all');
  const [rosterFilter, setRosterFilter] = useState('both');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [activeTeams, setActiveTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch active teams (teams from active leagues only)
  useEffect(() => {
    const fetchActiveTeams = async () => {
      setLoadingTeams(true);
      try {
        const [teamsData, leaguesData] = await Promise.all([
          unifiedApi.get('/api/teams'),
          unifiedApi.get('/api/leagues')
        ]);

        if (teamsData && leaguesData) {
          // Get active league IDs
          const activeLeagueIds = leaguesData
            .filter(league => league.isActive)
            .map(league => league.id);
          
          // Filter teams to only those in active leagues
          const teamsInActiveLeagues = teamsData.filter(team => 
            activeLeagueIds.includes(team.leagueId)
          );
          
          setActiveTeams(teamsInActiveLeagues);
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchActiveTeams();
  }, []);

  // Check if user has access - super admin should always have access
  const hasAccess = canAccessAdmin || isSuperAdmin;

  // Redirect if user doesn't have access
  if (!hasAccess) {
    return (
      <AdminLayout>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this area.
          </Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon color="primary" />
          Organization Members
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage all members and their roles across the organization.
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>View Members</InputLabel>
          <Select
            value={viewFilter}
            label="View Members"
            onChange={(e) => setViewFilter(e.target.value)}
          >
            <MenuItem value="all">All Members</MenuItem>
            <MenuItem value="elevated">Elevated Permissions</MenuItem>
            <MenuItem value="admins">Admins Only</MenuItem>
            <MenuItem value="managers">Managers</MenuItem>
            <MenuItem value="players">Players Only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Roster Status</InputLabel>
          <Select
            value={rosterFilter}
            label="Roster Status"
            onChange={(e) => setRosterFilter(e.target.value)}
          >
            <MenuItem value="both">All Players</MenuItem>
            <MenuItem value="rostered">Rostered</MenuItem>
            <MenuItem value="unrostered">Unrostered</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="not-invited">Not Invited</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Team (Active Seasons)</InputLabel>
          <Select
            value={teamFilter}
            label="Team (Active Seasons)"
            onChange={(e) => setTeamFilter(e.target.value)}
            disabled={loadingTeams}
          >
            <MenuItem value="all">All Teams</MenuItem>
            {activeTeams.map((team) => (
              <MenuItem key={team.id} value={team.id.toString()}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Member
          </Button>
        )}
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <OrganizationMembersTable 
        viewFilter={viewFilter}
        rosterFilter={rosterFilter}
        statusFilter={statusFilter}
        teamFilter={teamFilter}
        canEdit={isAdmin}
        refreshKey={refreshKey}
      />

      <AddMemberDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onMemberAdded={(result) => {
          setRefreshKey(prev => prev + 1);
          setSuccessMessage(result.message);
        }}
      />
    </AdminLayout>
  );
};

export default OrganizationMembersPage;
